"use client";

import { useState, useEffect, useCallback, useMemo } from "react";

const STORAGE_KEY_PREFIX = "forgeswap:hidden_tokens:";

export interface HiddenTokensAddresses {
  solana?: string | null;
  evm?: string | null; // Normalized to lowercase internally
}

/**
 * Hook to manage hidden tokens per wallet address.
 * Persists to localStorage so hidden tokens remain hidden across sessions.
 * Supports both Solana and EVM addresses with separate storage keys.
 */
export function useHiddenTokens(addresses: HiddenTokensAddresses) {
  // Separate state for each chain to avoid cross-chain interference
  const [solanaHidden, setSolanaHidden] = useState<string[]>([]);
  const [evmHidden, setEvmHidden] = useState<string[]>([]);

  const solanaKey = useMemo(
    () => (addresses.solana ? `${STORAGE_KEY_PREFIX}${addresses.solana}` : null),
    [addresses.solana]
  );

  const evmKey = useMemo(
    () => (addresses.evm ? `${STORAGE_KEY_PREFIX}${addresses.evm.toLowerCase()}` : null),
    [addresses.evm]
  );

  // Load Solana hidden tokens
  useEffect(() => {
    if (!solanaKey) {
      setSolanaHidden([]);
      return;
    }
    try {
      const stored = localStorage.getItem(solanaKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSolanaHidden(parsed);
          return;
        }
      }
    } catch {
      // Parse error
    }
    setSolanaHidden([]);
  }, [solanaKey]);

  // Load EVM hidden tokens
  useEffect(() => {
    if (!evmKey) {
      setEvmHidden([]);
      return;
    }
    try {
      const stored = localStorage.getItem(evmKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setEvmHidden(parsed);
          return;
        }
      }
    } catch {
      // Parse error
    }
    setEvmHidden([]);
  }, [evmKey]);

  // Combined set for isHidden checks
  const hiddenTokenIds = useMemo(
    () => [...solanaHidden, ...evmHidden],
    [solanaHidden, evmHidden]
  );

  const hideToken = useCallback(
    (tokenAddress: string, chain: "solana" | "evm") => {
      if (!tokenAddress) return;

      if (chain === "solana") {
        if (!solanaKey) return;
        setSolanaHidden((prev) => {
          if (prev.includes(tokenAddress)) return prev;
          const next = [...prev, tokenAddress];
          try {
            localStorage.setItem(solanaKey, JSON.stringify(next));
          } catch {
            // localStorage full or unavailable
          }
          return next;
        });
      } else {
        if (!evmKey) return;
        setEvmHidden((prev) => {
          if (prev.includes(tokenAddress)) return prev;
          const next = [...prev, tokenAddress];
          try {
            localStorage.setItem(evmKey, JSON.stringify(next));
          } catch {
            // localStorage full or unavailable
          }
          return next;
        });
      }
    },
    [solanaKey, evmKey]
  );

  const unhideToken = useCallback(
    (tokenAddress: string, chain: "solana" | "evm") => {
      if (!tokenAddress) return;

      if (chain === "solana") {
        if (!solanaKey) return;
        setSolanaHidden((prev) => {
          const next = prev.filter((id) => id !== tokenAddress);
          try {
            localStorage.setItem(solanaKey, JSON.stringify(next));
          } catch {
            // localStorage full or unavailable
          }
          return next;
        });
      } else {
        if (!evmKey) return;
        setEvmHidden((prev) => {
          const next = prev.filter((id) => id !== tokenAddress);
          try {
            localStorage.setItem(evmKey, JSON.stringify(next));
          } catch {
            // localStorage full or unavailable
          }
          return next;
        });
      }
    },
    [solanaKey, evmKey]
  );

  const isHidden = useCallback(
    (tokenAddress: string): boolean => {
      return hiddenTokenIds.includes(tokenAddress);
    },
    [hiddenTokenIds]
  );

  return {
    hiddenTokenIds,
    hideToken,
    unhideToken,
    isHidden,
    hiddenCount: hiddenTokenIds.length,
  };
}
